import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import LoadingScreen from '../components/LoadingScreen';
import { User } from './types';

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Safely convert a Firestore Timestamp | Date | ISO string | null → Date */
const toDate = (val: any): Date => {
  if (!val) return new Date();
  if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
  if (val instanceof Date) return val;
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  return new Date();
};

// ── Types ──────────────────────────────────────────────────────────────────────

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string, name: string, phone: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// ── Provider ───────────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user,    setUser]    = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const googleProvider = new GoogleAuthProvider();

  useEffect(() => {
    // Safety timeout — if Firebase auth doesn't respond in 8 s, stop loading
    // and show the app in logged-out state so users are never stuck.
    const safetyTimer = setTimeout(() => {
      setLoading(false);
      console.warn('AuthContext: Firebase auth timed out after 8 s — showing app unauthenticated.');
    }, 8000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      clearTimeout(safetyTimer);
      try {
        if (firebaseUser) {
          const userDocRef  = doc(db, 'users', firebaseUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          const data        = userDocSnap.data();

          const appUser: User = {
            id:             firebaseUser.uid,
            uid:            firebaseUser.uid,
            name:           firebaseUser.displayName || data?.name || '',
            phone:          data?.phone || '',
            walletBalance:  data?.walletBalance || 0,
            email:          firebaseUser.email || undefined,
            referralPoints: data?.referralPoints || 0,
            photoURL:       firebaseUser.photoURL || undefined,
            createdAt:      toDate(data?.createdAt),
            updatedAt:      toDate(data?.updatedAt),
            emailVerified:  firebaseUser.emailVerified,
          };

          if (!userDocSnap.exists()) {
            await setDoc(userDocRef, appUser);
          }

          setUser(appUser);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('AuthContext: user profile load error:', err);
        // Still show the app even if profile load fails
      } finally {
        setLoading(false);
      }
    });

    return () => { clearTimeout(safetyTimer); unsubscribe(); };
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Google Sign-In Error:', error);
    }
  };

  const signUp = async (email: string, password: string, name: string, phone: string) => {
    const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(firebaseUser);
    await setDoc(doc(db, 'users', firebaseUser.uid), {
      name, phone, email,
      walletBalance:  0,
      referralPoints: 0,
      createdAt:      new Date().toISOString(),
      updatedAt:      new Date().toISOString(),
      emailVerified:  firebaseUser.emailVerified,
    });
  };

  const logout = async () => { await firebaseSignOut(auth); };

  const value = useMemo(
    () => ({ user, loading, signIn, signInWithGoogle, signUp, logout }),
    [user, loading], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingScreen /> : children}
    </AuthContext.Provider>
  );
};
